import React from 'react'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'

import { Info2 } from '@aok/icon-functional'
import Tooltip from '../Tooltip/Tooltip'

import * as Component from './InfoIcon.styles'

import { useOutsideClickHandler } from 'hooks'
import { useTextContext } from 'hocs'

const InfoIcon = (props) => {
  const {
    infoIconId,
    tooltipId,
    arrow,
    children,
    content,
    dialogTitle,
    isMobile = false,
    // closeOnClickOutside,
    ...other
  } = props

  const [show, setShow] = React.useState(false)
  const tooltipContainerRef = React.useRef(null)
  // for adapting tooltip width to amount of inner text
  const [isLongText, setIsLongText] = React.useState(false)
  const tooltipContentRef = React.useCallback((node) => {
    if (node) {
      setIsLongText(node.textContent.length > 300)
    }
  }, [])

  const textContext = useTextContext()

  const handleClick = (e) => {
    e.stopPropagation()
    setShow(true)
  }

  const handleBackdropClick = (e) => {
    e.stopPropagation()
    // Ignore the events not coming from the "backdrop"
    if (e.target !== e.currentTarget) return
    setShow(false)
  }

  useOutsideClickHandler(tooltipContainerRef, () => {
    setShow(false)
  })

  // adapts tooltip width to length of child node text
  React.useEffect(() => {
    if (
      typeof children === 'object' &&
      children.hasOwnProperty('props') &&
      children.props.hasOwnProperty('dangerouslySetInnerHTML')
    ) {
      setIsLongText(children.props.dangerouslySetInnerHTML.__html.length > 300)
    }
  }, [children, setIsLongText])

  const getPlacement = () => {
    switch (arrow) {
      case 'left':
        return 'bottom-start'
      case 'right':
        return 'bottom-end'
      default:
        return 'bottom'
    }
  }

  return (
    <Component.Root { ...other }>
      {isMobile ? (
        <Component.InfoIcon
          id={ infoIconId }
          aria-describedby={ tooltipId }
          onClick={ handleClick }
          tabIndex='0'
          icon={ <Info2 /> }
        />
      ) : (
        <>
          <Tooltip
            leaveDelay={ 300 }
            title={
              typeof children === 'string' ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(children),
                  }}
                  ref={ tooltipContentRef }
                />
              ) : (
                <span ref={ tooltipContainerRef }>{children}</span>
              )
            }
            interactive
            arrow
            className={ isLongText ? 'long-text' : '' }
            placement={ getPlacement() }>
            <Component.IconWrapper>
              <Component.InfoIcon icon={ <Info2 /> } />
            </Component.IconWrapper>
          </Tooltip>
        </>
      )}
      {isMobile && show && (
        <Component.Backdrop onClick={ handleBackdropClick }>
          <Component.DialogContainer isFlat>
            {dialogTitle && (
              <Component.DialogTitle>
                <Component.DialogInfoIcon icon={ <Info2 /> } />
                <b>{dialogTitle}</b>
              </Component.DialogTitle>
            )}
            {content || children instanceof String ? (
              <Component.DialogContent
                id={ tooltipId }
                role='tooltip'
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(content ? content : children),
                }}
              />
            ) : (
              <Component.DialogContent id={ tooltipId } role='tooltip'>
                <>
                  {typeof children === 'string' && (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(children),
                      }}
                    />
                  )}
                  {typeof children === 'object' && children}
                </>
              </Component.DialogContent>
            )}
            <Component.DialogButton
              id='close-dialog'
              text={ textContext['infoIcon.btnText.close'] }
              onClick={ () => setShow(false) }
            />
          </Component.DialogContainer>
        </Component.Backdrop>
      )}
    </Component.Root>
  )
}

InfoIcon.defaultProps = {
  arrow: 'middle',
  closeOnClickOutside: false,
  withCookieContext: false,
}

InfoIcon.propTypes = {
  /** id of the info icon */
  infoIconId: PropTypes.string.isRequired,
  /** id of the tooltip container */
  tooltipId: PropTypes.string.isRequired,
  /** Tooltip content */
  children: PropTypes.node,
  /** Position of the arrow */
  arrow: PropTypes.oneOf(['left', 'middle', 'right']),
  /** Dialog title */
  dialogTitle: PropTypes.string,
  /**
   * only for desktop: if true desktop-tooltip stays open even on mouse-out
   * can be closed on click outside
   * functionality is required if tooltip content contains e.g. a button
   * **/
  closeOnClickOutside: PropTypes.bool,
}

export default InfoIcon
